import { Component, ElementRef, inject, signal, viewChild } from '@angular/core';
import { HelpCurationService, IHelpOdooImportStatus } from '@insolution/help-data-access';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'lib-help-odoo-import',
  imports: [TranslatePipe],
  templateUrl: './help-odoo-import.html',
  styleUrl: './help-odoo-import.scss',
})
export class HelpOdooImport {
  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  private readonly helpCurationService = inject(HelpCurationService);

  protected readonly importFile = signal<File | null>(null);
  protected readonly isImporting = signal(false);
  protected readonly importError = signal(false);
  protected readonly importStatus = signal<IHelpOdooImportStatus | null>(null);

  protected onFileSelected(event: Event): void {
    this.importFile.set((event.target as HTMLInputElement).files?.[0] ?? null);
    this.importStatus.set(null);
    this.importError.set(false);
  }

  protected async startImport(): Promise<void> {
    const file = this.importFile();
    if (!file || this.isImporting()) {
      return;
    }
    this.isImporting.set(true);
    this.importError.set(false);
    this.importStatus.set(null);
    try {
      const { importId, uploadUrl } = await this.helpCurationService.requestOdooImportUrl();
      await this.helpCurationService.uploadOdooExport(uploadUrl, file);
      await this.pollImportStatus(importId);
    } catch {
      this.importError.set(true);
    } finally {
      this.isImporting.set(false);
      // Reset the native <input>: it won't re-fire (change) for the same filename otherwise, so a
      // corrected file with the same name could not be uploaded again.
      this.importFile.set(null);
      const input = this.fileInput()?.nativeElement;
      if (input) {
        input.value = '';
      }
    }
  }

  private async pollImportStatus(importId: string): Promise<void> {
    for (let attempt = 0; attempt < 120; attempt++) {
      const status = await this.helpCurationService.getOdooImportStatus(importId);
      this.importStatus.set(status);
      if (status.status === 'complete' || status.status === 'failed') {
        if (status.status === 'failed') {
          this.importError.set(true);
        }
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 5000));
    }
  }
}
